import {
  Component, ViewChild, TemplateRef, Input, Output, EventEmitter, OnInit,
} from '@angular/core';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { get } from 'lodash';

import { UIKitIconRegistry } from '../../icon/icon.registry';
import { plusIcon } from '../../icon/plus/plus';
import { RestApiService, ModalService, LoggerService } from '../../service';
import { P } from '../../ui-kit';
import { Observable, of } from 'rxjs';

const MODAL_ID = 'lib-upload-modal';

// eslint-disable-next-line max-len
const defaultErrorMessage = 'Oops, an error has occurred. Please try again. If the problem persists - please contact support@proportunity.com.';

type FileType = 'annualStatements'
  | 'signedApplicationFile'
  | 'signedInterestOnlyDeclarationFile'
  | 'supportingFiles.identificationAndNationality'
  | 'supportingFiles.proofOfAddress'
  | 'supportingFiles.bankStatements'
  | 'supportingFiles.payslips'
  | 'otherFiles'
  | 'solicitor.file'
  | 'factFindFile'
  | 'firstChargeMortgage.valuationFile'
  | 'firstChargeMortgage.esisFile'
  | 'property.propertyFile'
  | 'offerPackFile'
  | 'proportunityLoan.esisFile';

type TargetType = 'application' | 'loan';

type FileId = string;

interface ApplicationFileUploadPatchBody {
  annualStatements?: FileId[];
  signedApplicationFile?: FileId[];
  signedInterestOnlyDeclarationFile?: FileId[];
  supportingFiles?: {
    identificationAndNationality?: FileId[];
    proofOfAddress?: FileId[];
    bankStatements?: FileId[];
    payslips?: FileId[];
  };
}

interface LoanFileUploadPatchBody extends ApplicationFileUploadPatchBody {
  otherFiles?: FileId[];
  solicitor?: {
    file?: FileId[];
  }
  factFindFile?: FileId[];
  firstChargeMortgage?: {
    valuationFile: FileId[];
    esisFile: FileId[];
  }
  property?: {
    propertyFile: FileId[];
  }
  proportunityLoan?: {
    esisFile: FileId[];
  }
  offerPackFile?: FileId[];
  filesv2?: FileId[];
}

export interface EventPayload {
  path?: FileType;
  loan?: P.LoanApplication;
  application?: P.Application;
  file?: P.ApplicationFile | P.LoanFile | P.S3File | null
}

@Component({
  selector: 'lib-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
})
export class UploadComponent implements OnInit {
  @Input() type: FileType | null = null;

  @Input() application: P.Application | P.LoanApplication | null = null;

  @Input() userId: string | null = null;

  @Input() target: TargetType = 'loan';

  @Input() title: string = 'Files(s)';

  @Input() theme: string = 'flat-accent';

  @Input() prefixIcon: string = 'plus';

  @Input() text: string = 'Upload';

  @Input() disabled: boolean = false;

  @Input() fullWidth: boolean = false;

  @Input() centered: boolean = false;

  @Input() small: boolean = false;

  @Input() accept: string = 'image/*,application/*,';

  @Output() success: EventEmitter<EventPayload> = new EventEmitter();

  @ViewChild('uploadRef', { static: true }) uploadRef: TemplateRef<unknown> | undefined;

  constructor(
    private restApi: RestApiService,
    private modal: ModalService,
    private logger: LoggerService,
    iconRegistry: UIKitIconRegistry,
  ) {
    iconRegistry.registerIcons([
      plusIcon,
    ]);
  }

  ngOnInit(): void {
    this.dropzoneConfig = {
      url: this.fileUploadEndpointUrl,
      headers: {
        'Cache-Control': null,
        Authorization: `Bearer ${this.restApi.accreditation?.token}`,
      },
      method: 'post',
      uploadMultiple: false,
      paramName: 'file',
      maxFiles: 1,
      maxFilesize: 50,
      acceptedFiles: this.accept,
    };
  }

  dropzoneConfig: DropzoneConfigInterface = {};

  file: P.ApplicationFile | P.LoanFile | P.S3File | null = null;

  toggle = {
    working: false,
  };

  startFileUploading() {
    this.toggle.working = true;

    this.updateDocumentTarget()
      .then(() => {
        this.modal.close(MODAL_ID);
      })
      .catch((error) => this.modal.open(error?.message || defaultErrorMessage, { id: 'modal-error' }))
      .finally(() => this.toggle.working = false);
  }

  onUploadError(error: { message: string } | undefined) {
    this.modal.close(MODAL_ID);

    this.logger.report(error);

    this.modal
      .open(error?.message || defaultErrorMessage, { id: 'modal-error' })
      .then(() => this.file = null);
  }

  onUploadSuccess(response: [File, P.ApplicationFile, ProgressEvent]) {
    this.file = response[1];
  }

  onUploadCancel() {
    this.file = null;
  }

  onUploadButtonClick() {
    if (!this.uploadRef) {
      throw new Error('uploadRef does not exist, check out view child in the template');
    }
    this.modal.open(this.uploadRef, { nav: false, id: MODAL_ID });
  }

  private get fileUploadEndpointUrl() {
    return `${this.restApi.url}/file/v2`;
  }

  private get isV2() {
    if (!this.type) {
      throw new Error('Type is required');
    }
    return this.target === 'application' || [
      'signedApplicationFile',
      'signedInterestOnlyDeclarationFile',
      'supportingFiles.identificationAndNationality',
      'supportingFiles.proofOfAddress',
      'supportingFiles.bankStatements',
      'supportingFiles.payslips',
    ].includes(this.type as string);
  }

  private async updateDocumentTarget() {
    if (this.target === 'application'
      && !this.application) {
      throw new Error('Application is required');
    }

    const application = this.application as P.Application;

    if (this.target === 'application') {
      // Backend doesn't treat patch body as P.Application when uploading files only,
      // so we had to inroduce a new "patch body" type for this particular case,
      // and use "as any" to cast it to P.Application essentially disabling TS check
      const body = this.getApplicationPatchBody() as any;
      await this.patchFileObservable.toPromise();
      const response = await this.restApi.updateApplication(application.id, body).toPromise()
      this.patchFileObservable = of();
      this.success.emit({ path: this.type as FileType, application: response });
    } else if (this.target === 'loan') {
      const body = this.getLoanPatchBody();
      await this.patchFileObservable.toPromise();
      const response = await this.restApi.patchIntermediaryApplication(application.id, body as any).toPromise()
      this.patchFileObservable = of();
      this.success.emit({ path: this.type as FileType, loan: response });
    } else {
      // throw new Error(`Unsupported target type: ${this.target}`);
      this.success.emit({ file: this.file });
    }
  }

  private patchFileObservable: Observable<P.ApplicationFile> = of();

  private getFiles(source: any, file: any, type?: FileType | 'filesv2') {
    const ids = (files: P.ApplicationFile[] | undefined): FileId[] => (files || []).map((file) => file.id);
    const files = [];
    const existing = get(source, (type || this.type) as FileType, []);
    const addFile = !type || (this.type === type || type === 'filesv2');

    if (this.isV2 || type === 'filesv2') {
      this.patchFileObservable = this.restApi.patchFile_admin(file.id, {
        ...file,
        fileType: this.type,
        ...(this.target === 'application' && { application: this.application?.id }),
        ...(this.target === 'loan' && { loan: this.application?.id }),
      });
      files.push(...ids(existing));
      if (addFile) {
        files.push(file.id);
      }
    } else {
      files.push(...existing);
      if (addFile) {
        files.push(file);
      }
    }
    return files;
  }

  private getApplicationPatchBody(): ApplicationFileUploadPatchBody {
    if (!this.file) {
      throw new Error('File can not be null');
    }
    const source = this.application as P.Application;
    const file = this.file as P.ApplicationFile;
    const type = this.type as FileType;

    return {
      ...(type === 'annualStatements' && {
        annualStatements: this.getFiles(source, file),
      }),
      ...(type === 'signedApplicationFile' && {
        signedApplicationFile: this.getFiles(source, file),
      }),
      ...(type === 'signedInterestOnlyDeclarationFile' && {
        signedInterestOnlyDeclarationFile: this.getFiles(source, file),
      }),
      ...(type.startsWith('supportingFiles') && {
        supportingFiles: {
          identificationAndNationality: this.getFiles(source, file, 'supportingFiles.identificationAndNationality'),
          bankStatements: this.getFiles(source, file, 'supportingFiles.bankStatements'),
          payslips: this.getFiles(source, file, 'supportingFiles.payslips'),
          proofOfAddress: this.getFiles(source, file, 'supportingFiles.proofOfAddress'),
        },
      }),
      ...(type === 'otherFiles' && {
        otherFiles: this.getFiles(source, file),
      }),
    };
  }

  private getLoanPatchBody(): LoanFileUploadPatchBody {
    const source = this.application as P.LoanApplication;
    const file = this.file as P.LoanFile;
    const type = this.type as FileType;

    return {
      ...(type === 'annualStatements' && {
        annualStatements: this.getFiles(source, file),
      }),
      ...(type === 'signedApplicationFile' && {
        signedApplicationFile: this.getFiles(source, file),
      }),
      ...(type === 'signedInterestOnlyDeclarationFile' && {
        signedInterestOnlyDeclarationFile: this.getFiles(source, file),
      }),
      ...(type.startsWith('supportingFiles') && {
        supportingFiles: {
          identificationAndNationality: this.getFiles(source, file, 'supportingFiles.identificationAndNationality'),
          bankStatements: this.getFiles(source, file, 'supportingFiles.bankStatements'),
          payslips: this.getFiles(source, file, 'supportingFiles.payslips'),
          proofOfAddress: this.getFiles(source, file, 'supportingFiles.proofOfAddress'),
        },
      }),
      ...(type === 'otherFiles' && {
        otherFiles: this.getFiles(source, file),
      }),
      ...(type === 'solicitor.file' && {
        solicitor: {
          ...source.solicitor,
          file: this.getFiles(source, file),
        },
      }),
      ...(type === 'factFindFile' && {
        factFindFile: this.getFiles(source, file),
      }),
      ...(type.startsWith('firstChargeMortgage') && {
        firstChargeMortgage: {
          ...source.firstChargeMortgage,
          valuationFile: this.getFiles(source, file, 'firstChargeMortgage.valuationFile'),
          esisFile: this.getFiles(source, file, 'firstChargeMortgage.esisFile'),
        },
      }),
      ...(type === 'property.propertyFile' && {
        property: {
          ...source.property,
          propertyFile: this.getFiles(source, file),
        },
      }),
      ...(type === 'proportunityLoan.esisFile' && {
        proportunityLoan: {
          ...source.proportunityLoan,
          esisFile: this.getFiles(source, file, 'proportunityLoan.esisFile'),
        },
      }),
      ...(type === 'offerPackFile' && {
        offerPackFile: this.getFiles(source, file),
      }),
      filesv2: this.getFiles(source, file, 'filesv2'),
    };
  }
}
